/**
 * DrillSpec — canonical JSON contract for the drill canvas.
 *
 * Demo Review Wave 1.1. This shape is the single source of truth that
 * drives:
 *   - The 2D SVG authoring canvas (apps/web/src/components/Drill/)
 *   - The read-only public-share view (Wave 3)
 *   - The PDF export view (Wave 3)
 *   - The future mobile renderer (deferred)
 *   - The future LLM primitive parser output (Wave 7)
 *
 * Coordinate system convention:
 *   - All entity / cone / arrow positions are in NORMALISED pitch units
 *     [0, 1] × [0, 1].
 *     x = 0 → left touchline (defending side, when applicable)
 *     x = 1 → right touchline
 *     y = 0 → far end (defending goal)
 *     y = 1 → near end (attacking goal)
 *   - Pitch dims (yards) come from `pitchPresetId` (lookup against
 *     PITCH_PRESETS in roi.ts) or `customPitch`.
 *
 * The renderer multiplies normalised coords by pixel dims at draw
 * time. Same DrillSpec renders identically on a phone or a 4K monitor
 * — that's the deterministic fidelity property the flipbook
 * recommendation §4 specifies.
 *
 * Ids are stable across saves so undo / animation / share-URLs can
 * key on them.
 */
import type { EntityType } from '../videoImport/types';

/** Schema version — bump when shape changes are not backwards-compat.
 *  The validator hard-rejects unknown versions. */
export const DRILL_SPEC_SCHEMA_VERSION = 1;

/** Normalised pitch coordinate, [0, 1] × [0, 1]. */
export type NormPoint = [number, number];

/** Animation pose hint for player tokens. Wave A.1 (UK launch) ships
 *  silhouettes for each value; the SVG renderer falls back to `idle`
 *  for unknown / missing values to stay backwards-compatible. */
export type DrillPose =
  | 'idle'
  | 'running'
  | 'kicking'
  | 'jumping'
  | 'falling';

/** A player / ball / official token on the canvas. Player numbers are
 *  display-only; identity is the stable `id`. */
export interface DrillEntity {
  id: string;
  type: EntityType;
  /** Normalised pitch position. */
  position: NormPoint;
  /** Display label — usually a jersey number or short name. */
  label: string;
  /** Optional position string (GK / CB / LW / etc) — feeds into the
   *  future formation-shape coaching point and the LLM coaching-
   *  point generator. */
  role?: string;
  /** Optional pose hint for the renderer. Default is `idle`. */
  pose?: DrillPose;
  /** Per-entity colour override — hex. When unset, the renderer
   *  uses the team's home/away colour from the session row. */
  color?: string;
}

/** A cone / mannequin / goal / hurdle / etc. — passive equipment, no label.
 *  Variant controls the SVG primitive. Goals + hurdles are passive equipment
 *  (not entities the renderer animates), so they share the cone shape family.
 *  Extended (May 2026) to cover full web1 parity minus per-player animation.
 */
export interface DrillCone {
  id: string;
  position: NormPoint;
  /** Visual variant. Default 'cone'.
   *  Coaching equipment family:
   *  - cone / flat / cone_disc / cone_training / cone_traffic — markers
   *  - mannequin / pole — vertical fixed obstacles
   *  - gate — two-cone narrow passage
   *  - corner_flag — corner-area indicator
   *  Goal family (rotation = facing direction):
   *  - goal_small / goal_mini — 5-yard practice goal
   *  - goal_7v7 / goal_9v9 — small-sided pitches
   *  - goal_full — regulation goal
   *  - crossbar — finishing-onto-crossbar target
   *  Training apparatus:
   *  - hurdle_mini / hurdle_med — sprint / coordination hurdles
   *  - agility_ladder — speed-ladder
   *  - bench — bench / step
   *  - rebounder — rebounder net
   */
  variant?:
    | 'cone' | 'flat' | 'cone_disc' | 'cone_training' | 'cone_traffic'
    | 'mannequin' | 'pole'
    | 'gate'
    | 'corner_flag'
    | 'goal_small' | 'goal_mini' | 'goal_7v7' | 'goal_9v9' | 'goal_full'
    | 'crossbar'
    | 'hurdle_mini' | 'hurdle_med' | 'agility_ladder' | 'bench' | 'rebounder';
  color?: string;
  /** Rotation in degrees (0-360, clockwise). Meaningful for
   *  directional variants — gates orient along the rotation axis;
   *  goals face the direction the rotation points to (default 0 =
   *  facing +y, the attacking goal-line). Symmetric variants
   *  (cone / flat) ignore rotation visually. */
  rotation?: number;
}

/** A movement arrow connecting two pitch points. Either anchored to
 *  entity ids (the renderer recomputes geometry when an entity moves)
 *  or to fixed normalised points. */
export interface DrillArrow {
  id: string;
  /** Start: an entity id OR a fixed point. */
  from:
    | { kind: 'entity'; entityId: string }
    | { kind: 'point'; position: NormPoint };
  to:
    | { kind: 'entity'; entityId: string }
    | { kind: 'point'; position: NormPoint };
  /** Visual variant. */
  style?: 'solid' | 'dashed' | 'dotted';
  /** Curvature [0, 1]. 0 = straight line; >0 bends through a Catmull-
   *  Rom control point offset perpendicular to the chord. */
  curvature?: number;
  /** Semantic kind — feeds coaching-point generation. */
  kind?: 'pass' | 'run' | 'dribble' | 'press' | 'cover';
  /** Hex colour override. */
  color?: string;
  /** Optional inline label rendered near the midpoint. */
  label?: string;
}

/** A free-form text label on the canvas — Wave A.4 (UK launch).
 *  Coach types coaching cues, scoring rules, zone names directly
 *  into a label that lives on the pitch. Survives undo/redo via
 *  the existing command pattern. */
export interface DrillText {
  id: string;
  position: NormPoint;
  /** The displayed text. May be multiline (\n). */
  content: string;
  /** Font size in yards (renderer scales by pxPerYd). Default 1.4. */
  fontSize?: number;
  /** Hex colour override; default white at 0.9 opacity. */
  color?: string;
  /** Optional rotation in degrees, clockwise. Default 0. */
  rotation?: number;
}

/** A zone / area marker — typically a rectangle for "press in this
 *  third" or a circle for a rondo grid. The demo's Lines tool with
 *  red dotted markers is a Zone in our model. */
export interface DrillZone {
  id: string;
  shape:
    | { kind: 'rect'; topLeft: NormPoint; bottomRight: NormPoint }
    | { kind: 'circle'; center: NormPoint; radiusNorm: number };
  /** Hex with optional alpha; e.g. '#C0392B40' for translucent red. */
  fill?: string;
  /** Hex stroke colour. */
  stroke?: string;
  strokeStyle?: 'solid' | 'dashed' | 'dotted';
  label?: string;
}

/** A snapshot of all entity positions at a single point in time
 *  during animation playback. Wave 4 wires the editor; the type is
 *  defined now so the schema doesn't churn. */
export interface DrillKeyframe {
  id: string;
  /** Total duration this keyframe occupies before the next, in
   *  seconds. Demo's KEYFRAME Duration field. */
  durationSec: number;
  /** Per-entity end positions at this keyframe. Entities not listed
   *  hold their previous position. Legacy v1 — entity-only. */
  positions: Record<string, NormPoint>;
  /** Per-entity sub-timing — start / end fraction of the keyframe's
   *  duration where this entity is moving. Demo's OBJECT Start/End
   *  Time slider. Entities not listed default to (0, 1). */
  objectTiming?: Record<string, { startPct: number; endPct: number }>;
  /** Optional coach-facing label ("Build-up", "Switch", "Finish"). */
  label?: string;
  /**
   * v2 — the new drill canvas animates rolePlayers + cones + arrows +
   * ball, not legacy entities. These per-overlay position maps let a
   * keyframe snapshot the full canvas state. The playback hook reads
   * v2 when present and falls back to `positions` otherwise.
   */
  rolePlayers?: Record<string, NormPoint>;
  cones?: Record<string, NormPoint>;
  /** Per-arrow endpoint pair — the arrow shape is invariant across
   *  keyframes; only its `from` + `to` move. */
  arrows?: Record<string, { from: NormPoint; to: NormPoint }>;
  /** Ball is rendered as a Ball-role player on the canvas; it can
   *  also be snapshotted independently here for clarity. Either field
   *  is accepted — `ball` wins if both are set. */
  ball?: NormPoint;
}

/** The canonical drill state. Stored as `session_screens.drill_spec`
 *  jsonb (Wave 2 migration 00052). */
export interface DrillSpec {
  schemaVersion: typeof DRILL_SPEC_SCHEMA_VERSION;
  /** Pitch dimensions. `pitchPresetId` references a preset in
   *  PITCH_PRESETS (roi.ts); `customPitch` is the escape hatch for
   *  non-standard sizes. The renderer prefers preset over custom. */
  pitchPresetId?: string | null;
  customPitch?: { width: number; length: number } | null;
  /** Whether to render the full pitch or a slice. The 'half_attacking'
   *  / 'half_defending' / 'quarter' modes show only that fraction so
   *  drills designed on a 30-yard grid don't look lost on a 115-yard
   *  pitch. Computed by the renderer; stored here for share-fidelity. */
  pitchView?: 'full' | 'half_attacking' | 'half_defending' | 'quarter';
  entities: DrillEntity[];
  cones: DrillCone[];
  arrows: DrillArrow[];
  zones: DrillZone[];
  /** Free-form text labels — Wave A.4. Coaching cues, scoring rules,
   *  zone names. Optional for back-compat: missing = []. */
  texts?: DrillText[];
  /** Animation keyframes, in playback order. Wave 4 wires the
   *  editor; empty array means a static drill. */
  keyframes: DrillKeyframe[];
  /** Render a 5-yard grid overlay on the pitch — Wave A.5. Default
   *  off; toolbar toggle in SessionScreenView flips it. */
  showGrid?: boolean;
}
